import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaSignOutAlt, FaMicrophone, FaStop } from "react-icons/fa";
import { DirectLine } from "botframework-directlinejs";
import * as sdk from "microsoft-cognitiveservices-speech-sdk";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import FemaleSpeakers from "../configs/femaleSpeakers";
import AvatarModel from "./AvatarModelFemale"; // Your avatar 3D model
import {URL} from "../urls";
import "./test-aisle-web.css";
import { getCurrentAccount, signOut } from "../azure-auth";

const config = require("../config.json");
const SpeechKey = config.SpeechKey;
const SpeechRegion = config.SpeechRegion;

// Helper function to strip markdown formatting characters
const stripMarkdown = (text) => {
  if (!text) return "";
  
  // Remove headers (##)
  let cleanText = text.replace(/#+\s/g, '');
  
  // Remove asterisks for bold/italic formatting
  cleanText = cleanText.replace(/\*\*/g, '');
  
  // Remove citation format like [1]: cite:1 "Citation-1"
  cleanText = cleanText.replace(/\[\d+\]:\s*cite:\d+\s*"[^"]*"/g, '');
  
  // Remove citation references like [1]
  cleanText = cleanText.replace(/\[\d+\]/g, '');
  
  return cleanText;
};

const ChatbotAFemale = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [directLine, setDirectLine] = useState(null);
  const [visemeIndex, setVisemeIndex] = useState(0);
  const [selectedVoice, setSelectedVoice] = useState("en-GB-AdaMultilingualNeural");
  const [listening, setListening] = useState(false);
  const [subtitles, setSubtitles] = useState("");
  const [error, setError] = useState("");
  const [isProcessingUserInput, setIsProcessingUserInput] = useState(false);
  const [isBotResponding, setIsBotResponding] = useState(false);
  // Add a new state for tracking conversation history
  const [conversationHistory, setConversationHistory] = useState([]);
  const lastUserMessageRef = useRef("");
  
  // Add refs to store speech synthesizer and recognizer for stopping functionality
  const synthesizerRef = useRef(null);
  const recognizerRef = useRef(null);
  // Add ref for media stream
  const mediaStreamRef = useRef(null);

  // Reference for the group container of the avatar model
  const modelRef = useRef();

  // Adjust camera position to focus on the shoulder/upper area of the avatar.
  const cameraPosition = [0, 1, 25]; 
  const lightIntensity = 0.7;

  useEffect(() => {
    const account = getCurrentAccount();
    if (!account) {
      navigate("/");
    } else {
      setUser(account);
    }
    setLoading(false);
  }, [navigate]);



  useEffect(() => {
    const fetchToken = async () => {
      try {
        const response = await fetch(URL.GET_TOKEN_TEST);
        const data = await response.json();
        if (data.token) setDirectLine(new DirectLine({ token: data.token }));
        else console.error("Error: No token received.");
      } catch (err) {
        console.error("Failed to fetch token:", err);
      }
    };
    fetchToken();
  }, []);

  
  // Cleanup media stream on component unmount
  useEffect(() => {
    return () => {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    if (directLine) {
      const subscription = directLine.activity$.subscribe((activity) => {
        // Only process messages from the bot (not from user)
        if (activity.from.id !== "user" && "text" in activity) {
          console.log("Bot response:", activity.text);
          
          if (typeof activity.text === "string") {
            // Reset the user input processing flag
            setIsProcessingUserInput(false);
            
            // CRITICAL CHECK: Ensure this is not an echo of the user's message
            if (activity.text.trim() !== lastUserMessageRef.current.trim()) {
              // Indicate the bot is now responding
              setIsBotResponding(true);
              
              // Clean the response text by stripping markdown formatting
              const cleanedText = stripMarkdown(activity.text);
              
              // Update subtitles with cleaned bot response
              setSubtitles(cleanedText);
              
              // Add to conversation history
              setConversationHistory(prev => [
                ...prev, 
                { role: 'bot', text: cleanedText }
              ]);
              
              // Speak the cleaned bot response
              synthesizeSpeech(cleanedText);
            } else {
              console.log("Prevented echo of user message:", activity.text);
              setIsBotResponding(false);
            }
          }
        }
      });
      return () => subscription.unsubscribe();
    }
  }, [directLine, selectedVoice]);

  const synthesizeSpeech = (text) => {
    // EXTRA CHECK: Don't synthesize if the text matches the user's last input
    if (text.trim() === lastUserMessageRef.current.trim()) {
      console.log("Prevented speech synthesis of user's input:", text);
      setIsBotResponding(false);
      return;
    }
    
    const speechConfig = sdk.SpeechConfig.fromSubscription(SpeechKey, SpeechRegion);
    const synthesizer = new sdk.SpeechSynthesizer(speechConfig);
    
    // Store the synthesizer reference for stop functionality
    synthesizerRef.current = synthesizer;

    // Adjust language to match the selected voice (if needed)
    const languageCode = selectedVoice.split("-").slice(0, 2).join("-");
    
    // Escape special XML characters in the text
    const escapedText = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;");
    
    const ssml = `<speak version='1.0' xml:lang='${languageCode}' xmlns='http://www.w3.org/2001/10/synthesis' xmlns:mstts='http://www.w3.org/2001/mstts'>
        <voice name='${selectedVoice}'>
            <mstts:viseme type='redlips_front'/>
            ${escapedText}
        </voice>
    </speak>`;

    synthesizer.visemeReceived = (s, e) => {
      console.log(`Viseme ID: ${e.visemeId}, Offset: ${e.audioOffset}`);
      setTimeout(() => setVisemeIndex(e.visemeId), e.audioOffset / 10000);
    };

    synthesizer.speakSsmlAsync(
      ssml,
      (result) => {
        if (result.reason === sdk.ResultReason.SynthesizingAudioCompleted) {
          console.log("Speech synthesized.");
          setListening(false);
          setVisemeIndex(0);
          setIsBotResponding(false); // Reset bot responding flag
        } else {
          console.error("Speech synthesis error:", result.errorDetails);
          setError("Speech synthesis error");
          setIsBotResponding(false); // Reset bot responding flag
        }
        synthesizer.close();
        synthesizerRef.current = null;
      },
      (err) => {
        console.error("Error synthesizing:", err);
        setError("Error synthesizing speech");
        setIsBotResponding(false); // Reset bot responding flag
        synthesizer.close();
        synthesizerRef.current = null;
      }
    );
  };

  // Function to get browser microphone access
  const getBrowserMicrophone = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000
        } 
      });
      mediaStreamRef.current = stream;
      return stream;
    } catch (error) {
      console.error("Error accessing browser microphone:", error);
      setError("Could not access browser microphone. Please check permissions.");
      throw error;
    }
  };

  const startListening = async () => {
    if (isBotResponding) return; // Don't start listening if bot is currently responding
    
    try {
      setListening(true);
      // Mark that we're processing user input
      setIsProcessingUserInput(true);
      // CRITICAL: Clear the subtitles immediately when we start listening
      setSubtitles("");
      setError("");

      // Get browser microphone access
      const stream = await getBrowserMicrophone();
      
      const speechConfig = sdk.SpeechConfig.fromSubscription(SpeechKey, SpeechRegion);
      
      // Create audio config from browser microphone stream
      const audioConfig = sdk.AudioConfig.fromStreamInput(stream);
      const recognizer = new sdk.SpeechRecognizer(speechConfig, audioConfig);
      
      // Store the recognizer reference for stop functionality
      recognizerRef.current = recognizer;

      recognizer.recognizing = (_, event) => {
        // Log to console only, NEVER update subtitles with user input
        console.log(`Recognizing: ${event.result.text}`);
        // CRITICAL: Force subtitles to stay empty during recognition
        setSubtitles("");
      };
      
      recognizer.recognized = (_, event) => {
        if (event.result.reason === sdk.ResultReason.RecognizedSpeech) {
          const recognizedText = event.result.text;
          console.log(`Recognized: ${recognizedText}`);
          
          // Store the user's message to prevent echo
          lastUserMessageRef.current = recognizedText;
          
          // Add to conversation history
          setConversationHistory(prev => [
            ...prev,
            { role: 'user', text: recognizedText }
          ]);
          
          // CRITICAL: NEVER show the recognized text in subtitles
          setSubtitles("");
          
          // Send to bot without updating subtitles with user input
          sendToBot(recognizedText);
          
          recognizer.stopContinuousRecognitionAsync(() => {
            recognizer.close();
            recognizerRef.current = null;
            // Stop the media stream
            if (mediaStreamRef.current) {
              mediaStreamRef.current.getTracks().forEach(track => track.stop());
              mediaStreamRef.current = null;
            }
          }, () => {
            recognizer.close();
            recognizerRef.current = null;
            // Stop the media stream
            if (mediaStreamRef.current) {
              mediaStreamRef.current.getTracks().forEach(track => track.stop());
              mediaStreamRef.current = null;
            }
          });
        } else {
          setListening(false);
          setIsProcessingUserInput(false);
          // Stop the media stream
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
          }
        }
      };
      
      recognizer.canceled = (_, event) => {
        console.log(`Recognition canceled: ${event.errorDetails}`);
        setListening(false);
        setIsProcessingUserInput(false);
        recognizer.stopContinuousRecognitionAsync(() => {
          recognizer.close();
          recognizerRef.current = null;
          // Stop the media stream
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
          }
        }, () => {
          recognizer.close();
          recognizerRef.current = null;
          // Stop the media stream
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
          }
        });
      };
      
      recognizer.sessionStopped = () => {
        console.log("Recognition session stopped");
        setListening(false);
        recognizer.stopContinuousRecognitionAsync(() => {
          recognizer.close();
          recognizerRef.current = null;
          // Stop the media stream
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
          }
        }, () => {
          recognizer.close();
          recognizerRef.current = null;
          // Stop the media stream
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
          }
        });
      };
      
      recognizer.startContinuousRecognitionAsync();
      
    } catch (error) {
      console.error("Error starting browser microphone listening:", error);
      setListening(false);
      setIsProcessingUserInput(false);
      setError("Failed to start listening. Please check microphone permissions.");
    }
  };

  // Function to stop speech synthesis and/or speech recognition
  const stopSpeech = () => {
    // Stop speech synthesis if it's active
    if (synthesizerRef.current) {
      console.log("Stopping speech synthesis");
      synthesizerRef.current.close();
      synthesizerRef.current = null;
      setIsBotResponding(false);
      setVisemeIndex(0);
    }
    
    // Stop speech recognition if it's active
    if (recognizerRef.current) {
      console.log("Stopping speech recognition");
      recognizerRef.current.stopContinuousRecognitionAsync(
        () => {
          recognizerRef.current.close();
          recognizerRef.current = null;
          setListening(false);
          setIsProcessingUserInput(false);
          // Stop the media stream
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
          }
        },
        (err) => {
          console.error("Error stopping recognition:", err);
          recognizerRef.current.close();
          recognizerRef.current = null;
          setListening(false);
          setIsProcessingUserInput(false);
          // Stop the media stream
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
          }
        }
      );
    }
    
    // Stop media stream if it's active
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
  };

  // Function to send text input directly (for enhanced two-way communication)
  const sendTextInput = (e) => {
    e.preventDefault();
    const textInput = e.target.textInput.value.trim();
    if (textInput) {
      // Store the user's message to prevent echo
      lastUserMessageRef.current = textInput;
      
      // Add to conversation history
      setConversationHistory(prev => [
        ...prev,
        { role: 'user', text: textInput }
      ]);
      
      // Send to bot
      sendToBot(textInput);
      
      // Clear the input field
      e.target.textInput.value = "";
    }
  };

  const sendToBot = (message) => {
    if (directLine) {
      // CRITICAL: Always keep subtitles empty during user input processing
      setSubtitles(""); // Keep subtitles empty
      setIsProcessingUserInput(true);
      
      // Store the user message to prevent echo later
      lastUserMessageRef.current = message;
      
      // Create a blank activity that doesn't include the user's speech
      directLine
        .postActivity({ 
          from: { id: "user" }, 
          type: "message", 
          text: message,  // This won't be displayed or spoken, only sent to bot
          // Add a flag to ensure this is treated as user input
          channelData: { isUserSpeechInput: true, preventEcho: true }
        })
        .subscribe(
          (id) => {
            console.log(`Sent to Bot: ${id}`);
            // CRITICAL: Double-check subtitles are still empty
            setSubtitles("");
          },
          (err) => {
            console.error("Error sending message:", err);
            setError("There was a problem sending your message.");
            setIsProcessingUserInput(false);
          }
        );
    }
  };

  if (loading) return <div>Loading...</div>;
  if (!user) return null;

  return (
      <div style={{ 
        display: "flex", 
        flexDirection: "column", 
        alignItems: "center",
        padding: "10px",
        minHeight: "100vh",
        boxSizing: "border-box"
      }}>
      <img 
        src="/aisleaiagents.png" 
        alt="aisleaiagents Logo" 
        className="logo" 
        onClick={() => navigate('/dashboard')} 
        style={{ 
          cursor: 'pointer', 
          position: 'absolute', 
          top: '10px', 
          left: '10px',
          maxWidth: '200px',
          height: 'auto'
        }} 
      />
      <h1>Test Aisle Web - Zara</h1>
      {/* <button  className="logout-button" onClick={logout} style={{ marginBottom: "10px" }}>
        <FaSignOutAlt /> Log Out
      </button> */}

      <div style={{ marginBottom: "10px", display: "flex", flexDirection: "column", alignItems: "center" }}>
        {/* <label htmlFor="voice-select" style={{ marginBottom: "5px", fontWeight: "bold", color: "gray", fontSize: "14px" }}>
          Select Voice:
        </label> */}
        <select
          id="voice-select"
          value={selectedVoice}
          onChange={(e) => setSelectedVoice(e.target.value)}
          style={{
            padding: "8px 12px",
            borderRadius: "10px",
            border: "2px solid #b100cd",
            fontSize: "14px",
            width: "90%",
            maxWidth: "300px",
            backgroundColor: "#fff",
            color: "#333",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            transition: "border-color 0.3s ease, box-shadow 0.3s ease",
            minWidth: "280px"
          }}
          onFocus={(e) => {
            e.target.style.borderColor = "#ffaeff";
            e.target.style.boxShadow = "0 0 10px rgba(255, 174, 255, 0.5)";
          }}
          onBlur={(e) => {
            e.target.style.borderColor = "#b100cd";
            e.target.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";
          }}
        >
          {FemaleSpeakers.map((voice) => (
            <option key={voice.value} value={voice.value}>
              {voice.label}
            </option>
          ))}
        </select>
      </div>

      {/* 3D Avatar Container for Shoulder/Upper Position */}
      <div
        style={{
          width: "min(440px, 90vw)",
          height: "min(440px, 90vw)",
          maxWidth: "440px",
          maxHeight: "440px",
          backgroundColor: "rgba(0,0,0,0.4)",
          borderRadius: "10px",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 0 30px rgba(217, 48, 255, 0.7)",
          marginBottom: "20px",
        }}
      >

        <Canvas
          camera={{ position: cameraPosition, fov: 36, near: 0.1, far: 1000 }}
          shadows
        >
          <ambientLight intensity={lightIntensity} />
          <spotLight
            position={[15, 10, 35]}
            angle={0.3}
            penumbra={1}
            castShadow
            intensity={lightIntensity}
          />
          <pointLight position={[10, 10, 10]} intensity={lightIntensity * 1000} />
          
          {/* Group with avatar model positioned and scaled to show the shoulder/upper area */}
          <group ref={modelRef} scale={0.11} position={[0, -5, 0]} rotation={[0, 0, 0]}>
            <AvatarModel visemeIndex={visemeIndex} />
          </group>
          
          <OrbitControls
            enableZoom={true}
            enablePan={true}
            enableRotate={true}
            target={[0, 0, 0]}
            minDistance={1}
            maxDistance={10}
          />
        </Canvas>
      </div>
      <button className="Btn" onClick={() => navigate("/test-aisle-web")}>
        <div className="sign">
          {/* Back Arrow Icon */}
          <svg viewBox="0 0 512 512">
             <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288 416 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-306.7 0L214.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"/>
          </svg>
        </div>
        <div className="text">Back</div>
      </button>
      
      {/* Display area for subtitles - ONLY shows bot responses, NEVER user input */}
      <div
        style={{
          marginBottom: "20px",
          width: "90%",
          maxWidth: "500px",
          textAlign: "center",
          fontSize: "clamp(14px, 4vw, 18px)",
          padding: "10px",
          backgroundColor: "rgba(0, 0, 0, 0.5)", 
          color: "white",
          borderRadius: "5px",
          minHeight: "50px",
          wordWrap: "break-word",
          boxSizing: "border-box"
        }}
        aria-live="polite"
      >

        {/* CRITICAL: Never show subtitles during user input processing */}
        {isProcessingUserInput || listening ? "" : subtitles}
      </div>
      
      {error && (
        <div style={{ color: "red", marginBottom: "20px" }}>{error}</div>
      )}
      
      {/* Speech Controls */}
        <div style={{ 
          display: "flex", 
          gap: "10px", 
          marginBottom: "20px",
          flexWrap: "wrap",
          justifyContent: "center",
          width: "90%",
          maxWidth: "400px"
        }}>
        <button
          onClick={startListening}
          disabled={listening || isProcessingUserInput || isBotResponding}
          style={{
            backgroundColor: (listening || isProcessingUserInput || isBotResponding) ? "#888" : "#b100cd",
            color: "#fff",
            border: "none",
            borderRadius: "15px",
            padding: "clamp(8px, 2vw, 12px) clamp(16px, 4vw, 20px)",
            fontSize: "clamp(14px, 3vw, 16px)",
            cursor: (listening || isProcessingUserInput || isBotResponding) ? "not-allowed" : "pointer",
            transition: "background-color 0.3s ease",
            boxShadow: "0 4px 6px rgba(237, 134, 247, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flex: "1",
            minWidth: "120px",
            justifyContent: "center"
          }}
        >
          <FaMicrophone /> {listening ? "Listening..." : "Speak"}
        </button>
        
        {/* New Stop Button */}
        {/* <button
          onClick={stopSpeech}
          disabled={!listening && !isBotResponding}
          style={{
            backgroundColor: (!listening && !isBotResponding) ? "#888" : "#f44336",
            color: "#fff",
            border: "none",
            borderRadius: "15px",
            padding: "10px 20px",
            fontSize: "16px",
            cursor: (!listening && !isBotResponding) ? "not-allowed" : "pointer",
            transition: "background-color 0.3s ease",
            boxShadow: "0 4px 6px rgba(244, 67, 54, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}
        >
          <FaStop /> Stop
        </button> */}
      </div>
      
      {/* Text Input Form for Enhanced Two-Way Communication */}
      <form 
        onSubmit={sendTextInput}
        style={{
          display: "flex",
          width: "90%",
          maxWidth: "500px",
          marginBottom: "20px",
          gap: "10px",
          flexDirection: window.innerWidth <= 768 ? "column" : "row",
          alignItems: "center",
          boxSizing: "border-box"
        }}
      >
        <input
          type="text"
          name="textInput"
          placeholder="Type your message here..."
          disabled={isProcessingUserInput || isBotResponding}
          style={{
            flex: 1,
            padding: "clamp(8px, 2vw, 12px)",
            borderRadius: "5px",
            border: "1px solid #ccc",
            fontSize: "clamp(14px, 3vw, 16px)",
            minWidth: "200px",
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box"
          }}
        />
        <button
          type="submit"
          disabled={isProcessingUserInput || isBotResponding}
          style={{
            backgroundColor: (isProcessingUserInput || isBotResponding) ? "#888" : "#b100cd",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            padding: "clamp(8px, 2vw, 12px) clamp(16px, 4vw, 20px)",
            fontSize: "clamp(14px, 3vw, 16px)",
            cursor: (isProcessingUserInput || isBotResponding) ? "not-allowed" : "pointer",
            transition: "background-color 0.3s ease",
            boxShadow: "0 4px 6px rgba(237, 134, 247, 0.1)",
            minWidth: "80px",
            whiteSpace: "nowrap"
          }}
        >
          Send
        </button>
      </form>
       
      
      {/* Conversation History Display */}
      <div
        style={{
          width: "90%",
          maxWidth: "500px",
          maxHeight: "300px",
          overflowY: "auto",
          border: "1px solid #ccc",
          borderRadius: "5px",
          padding: "clamp(8px, 2vw, 12px)",
          marginBottom: "20px",
          color: "#333",
          boxSizing: "border-box"
        }}
      >

        <h3 style={{ textAlign: "center", marginTop: 0 , color: "#ffff"}}>Conversation History</h3>
        {conversationHistory.length === 0 ? (
          <p style={{ textAlign: "center", color: "#888" }}>No conversation yet. Start talking!</p>
        ) : (
          conversationHistory.map((item, index) => (
            <div 
              key={index}
              style={{
                padding: "8px",
                margin: "5px 0",
                borderRadius: "5px",
                backgroundColor: item.role === 'user' ? "#f0f0f0" : "#ffaeff",
                textAlign: item.role === 'user' ? "right" : "left"
              }}
            >
              <strong>{item.role === 'user' ? 'You' : 'Agent'}: </strong>
              {item.text}
            </div>
          ))
        )}
      </div>
      
      {/* Status Indicator */}
      <div style={{ 
        padding: "clamp(4px, 1vw, 6px) clamp(12px, 3vw, 15px)", 
        borderRadius: "20px", 
        backgroundColor: listening ? "#4CAF50" : 
                        isProcessingUserInput ? "#FF9800" : 
                        isBotResponding ? "#2196F3" : 
                        "#9E9E9E",
        color: "white",
        fontWeight: "bold",
        fontSize: "clamp(12px, 3vw, 14px)",
        marginBottom: "20px",
        textAlign: "center",
        maxWidth: "200px"
      }}>
        {listening ? "Listening" : 
         isProcessingUserInput ? "Processing" : 
         isBotResponding ? "Speaking" : 
         "Ready"}
      </div>
    </div>
  );
};

export default ChatbotAFemale;